/**
 * EPIC-DM01-G: Demo Share Hook
 * Manages share card generation and sharing functionality
 */

import { useState, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';

export interface ShareCardPreview {
  format: '1:1' | '9:16';
  insight: string;
  focusArea: string;
  overallScore?: number;
  generatedAt: string;
  branding: {
    logo: string;
    tagline: string;
  };
}

export interface GenerateShareResult {
  ok: boolean;
  share_url?: string;
  artifact_id?: string;
  preview_data?: ShareCardPreview;
  error?: string;
  reason?: string;
}

export interface RevokeShareResult {
  ok: boolean;
  revoked_count?: number;
  error?: string;
}

// API helper
async function getAccessToken(): Promise<string> {
  const { data: { session } } = await supabase.auth.getSession();
  if (!session?.access_token) {
    throw new Error('Not authenticated');
  }
  return session.access_token;
}

async function callEdgeFunction<T>(
  functionName: string,
  method: 'GET' | 'POST' = 'POST',
  body?: unknown
): Promise<T> {
  const token = await getAccessToken();
  
  const url = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/${functionName}`;

  const response = await fetch(url, {
    method,
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
    },
    ...(body ? { body: JSON.stringify(body) } : {}),
  });

  const data = await response.json();
  
  if (!response.ok && !data.ok) {
    const error = new Error(data.error || `Request failed with status ${response.status}`);
    (error as any).reason = data.reason;
    throw error;
  }

  return data as T;
}

export function useDemoShare(sessionId: string) {
  const [isGenerating, setIsGenerating] = useState(false);
  const [isRevoking, setIsRevoking] = useState(false);
  const [shareUrl, setShareUrl] = useState<string | null>(null);
  const [previewData, setPreviewData] = useState<ShareCardPreview | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [errorReason, setErrorReason] = useState<string | null>(null);

  // Generate share card and get share URL
  const generateShareCard = useCallback(async (
    format: '1:1' | '9:16' = '1:1'
  ): Promise<GenerateShareResult> => {
    if (!sessionId) {
      return { ok: false, error: 'No session ID provided' };
    }

    setIsGenerating(true);
    setError(null);
    setErrorReason(null);

    try {
      // Emit telemetry for share opened
      console.log('[useDemoShare] Telemetry:', {
        event: 'demo_share_opened',
        session_id: sessionId,
        format,
      });

      const result = await callEdgeFunction<GenerateShareResult>(
        'demo-share-generate',
        'POST',
        { demo_session_id: sessionId, format }
      );

      if (result.ok && result.share_url) {
        setShareUrl(result.share_url);
        setPreviewData(result.preview_data || null);
        
        // Emit telemetry
        console.log('[useDemoShare] Telemetry:', {
          event: 'demo_share_generated',
          session_id: sessionId,
          format,
          artifact_id: result.artifact_id,
        });
      } else {
        setError(result.error || 'Failed to generate share card');
        setErrorReason(result.reason || null);
      }

      return result;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to generate share card';
      const reason = (err as any)?.reason || null;
      setError(errorMessage);
      setErrorReason(reason);
      return { ok: false, error: errorMessage, reason };
    } finally {
      setIsGenerating(false);
    }
  }, [sessionId]);

  // Revoke all share tokens for this session
  const revokeShare = useCallback(async (): Promise<RevokeShareResult> => {
    if (!sessionId) {
      return { ok: false, error: 'No session ID provided' };
    }

    setIsRevoking(true);
    setError(null);

    try {
      const result = await callEdgeFunction<RevokeShareResult>(
        'demo-share-revoke',
        'POST',
        { demo_session_id: sessionId }
      );

      if (result.ok) {
        setShareUrl(null);
        setPreviewData(null);
      }

      return result;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to revoke share';
      setError(errorMessage);
      return { ok: false, error: errorMessage };
    } finally {
      setIsRevoking(false);
    }
  }, [sessionId]);

  // Copy share URL to clipboard
  const copyShareUrl = useCallback(async (): Promise<boolean> => {
    if (!shareUrl) return false;

    try {
      await navigator.clipboard.writeText(shareUrl);
      
      // Emit telemetry
      console.log('[useDemoShare] Telemetry:', {
        event: 'demo_share_completed',
        session_id: sessionId,
        method: 'copy',
      });

      return true;
    } catch {
      return false;
    }
  }, [shareUrl, sessionId]);

  // Use native share API (if available)
  const nativeShare = useCallback(async (): Promise<boolean> => {
    if (!shareUrl) return false;

    // Check if Web Share API is available
    if (!navigator.share) {
      return false;
    }

    try {
      await navigator.share({
        title: 'My Golf Swing Analysis',
        text: previewData?.insight || 'Check out my swing analysis!',
        url: shareUrl,
      });

      // Emit telemetry
      console.log('[useDemoShare] Telemetry:', {
        event: 'demo_share_completed',
        session_id: sessionId,
        method: 'native',
      });

      return true;
    } catch (err) {
      // User cancelled or error
      if ((err as Error)?.name === 'AbortError') {
        return false;
      }
      console.error('[useDemoShare] Native share failed:', err);
      return false;
    }
  }, [shareUrl, previewData, sessionId]);

  // Check if native share is available
  const hasNativeShare = typeof navigator !== 'undefined' && 'share' in navigator;

  return {
    // State
    isGenerating,
    isRevoking,
    shareUrl,
    previewData,
    error,
    errorReason,
    hasNativeShare,

    // Actions
    generateShareCard,
    revokeShare,
    copyShareUrl,
    nativeShare,

    // Reset
    clearError: useCallback(() => {
      setError(null);
      setErrorReason(null);
    }, []),
  };
}
